// src/modules/documents/generators/generateMarka.js
//
// Native geometri MARKA alanı üretici. Firma + proje sorumlusu bilgilerini
// (Nef header düzeninde) MTEXT bloklarıyla çizer. Logo/görsel ERTELENDİ (OLE2);
// şimdilik yalnızca metin.
//
// Marka hücresi (title block üst hücresi): x:15-885, y:1138-1418 (870x280).
//   sol  : logo alanı (şimdilik boş — ileride OLE/görsel)
//   orta : firma bloğu (tam ad + adres + tel + V.D.)
//   sağ  : proje sorumlusu bloğu (ad + ünvan + TC + Gsm)
//
// CAD Oluşturucu 'Marka' modu (canlı önizleme) + generateBaslik (Marka hücresine
// yerleşim) aynı fonksiyonu kullanır → tek kaynak.

const F  = '\\fTimes New Roman|b0|i0|c162|p18;'; // normal
const FB = '\\fTimes New Roman|b1|i0|c162|p18;'; // kalın

// MTEXT özel karakter kaçışı (\, {, }, ; MTEXT kontrol karakterleridir)
function esc(s) {
  return String(s == null ? '' : s)
    .replace(/\\/g, '\\\\')
    .replace(/\{/g, '\\{')
    .replace(/\}/g, '\\}')
    .replace(/;/g, '\\;');
}

// MTEXT wrap tahmini — Times New Roman ortalama karakter genişliği ≈ fh * 0.55.
// Array entry sayısı değil, görsel satır sayısını döner.
function estimateVisualLines(lines, availW, fh) {
  const cw = fh * 0.55;
  const maxChars = Math.max(1, Math.floor(availW / cw));
  return lines.reduce((n, l) => n + Math.max(1, Math.ceil(l.text.length / maxChars)), 0);
}

// Satır listesinden MTEXT gövdesi (\P ile ayrılmış, satır başına kalın/normal font).
function mtextBody(lines) {
  return '{' + lines.map(l => (l.bold ? FB : F) + esc(l.text)).join('\\P') + '}';
}

function mtext(handle, x, y, lines, { height = 13, width = 400, layer = 'Doc_Baslik', attach = 1 } = {}) {
  return {
    type: 'MTEXT',
    handle,
    ownerHandle: '1F',
    layer,
    position: { x, y, z: 0 },
    height,
    width,
    attachmentPoint: attach, // 1: top-left, 5: middle-center
    drawingDirection: 5,
    text: mtextBody(lines),
  };
}

function circle(handle, cx, cy, radius, layer) {
  return {
    type: 'CIRCLE',
    handle,
    ownerHandle: '1F',
    layer,
    center: { x: cx, y: cy, z: 0 },
    radius,
  };
}

// (cx,cy) merkezli, yarı-kenarı hs olan kareyi 4 LINE olarak ekler.
function pushSquare(entities, hFn, cx, cy, hs, layer) {
  const x0 = cx - hs, x1 = cx + hs, y0 = cy - hs, y1 = cy + hs;
  entities.push(line(hFn(), x0, y0, x1, y0, layer));
  entities.push(line(hFn(), x1, y0, x1, y1, layer));
  entities.push(line(hFn(), x1, y1, x0, y1, layer));
  entities.push(line(hFn(), x0, y1, x0, y0, layer));
}

function line(handle, x1, y1, x2, y2, layer) {
  return {
    type: 'LINE',
    handle,
    ownerHandle: '1F',
    layer,
    vertices: [{ x: x1, y: y1, z: 0 }, { x: x2, y: y2, z: 0 }],
  };
}

/**
 * Firma bloğu satırları (orta blok) — boş alanlar atlanır.
 */
function firmaLines(firma = {}) {
  const lines = [];
  const ad = firma.firmaTamAdi || firma.firmaAdi;
  if (ad) lines.push({ text: ad, bold: true });
  if (firma.adres) lines.push({ text: firma.adres });
  if (firma.tel) lines.push({ text: 'Tel        : ' + firma.tel });
  // "Bafra V.D.  ; 630 043 37 79" düzeni
  const vd = firma.vergiDairesi, vn = firma.vergiNo;
  if (vd && vn) lines.push({ text: `${vd} V.D.  ; ${vn}` });
  else if (vn) lines.push({ text: 'V.D. No : ' + vn });
  else if (vd) lines.push({ text: vd + ' V.D.' });
  return lines;
}

/**
 * Proje sorumlusu bloğu satırları (sağ blok).
 */
function sorumluLines(sorumlu = {}) {
  const lines = [];
  if (sorumlu.adiSoyadi) lines.push({ text: sorumlu.adiSoyadi, bold: true });
  if (sorumlu.unvan) lines.push({ text: sorumlu.unvan });
  if (sorumlu.odaNo) lines.push({ text: 'Oda No : ' + sorumlu.odaNo });
  if (sorumlu.tcNo) lines.push({ text: 'Tc No : ' + sorumlu.tcNo });
  if (sorumlu.gsm) lines.push({ text: 'Gsm   : ' + sorumlu.gsm });
  return lines;
}

/**
 * Native marka alanı entity'lerini üretir.
 * @param {{firma:object, sorumlu:object}} markaData
 * @param {object} [opts]
 * @param {number} [opts.originX=15]   Marka hücresi sol-alt X
 * @param {number} [opts.originY=1138] Marka hücresi sol-alt Y
 * @param {number} [opts.width=870]
 * @param {number} [opts.height=280]
 * @param {boolean} [opts.drawFrame=true] Hücre çerçevesi çiz (standalone önizleme için;
 *                                        generateBaslik'te çerçeve zaten var → false)
 * @param {string} [opts.layer='Doc_Baslik']
 * @param {string} [opts.handlePrefix='MK'] Entity handle öneki
 * @returns {Array} DXF entity dizisi (MTEXT + opsiyonel LINE çerçeve)
 */
export function buildMarkaEntities(markaData = {}, opts = {}) {
  const {
    originX = 15, originY = 1138, width = 870, height = 280,
    drawFrame = true, layer = 'Doc_Baslik', handlePrefix = 'MK',
    // 0: yok, 1: iç şekil, 2: iç+dış şekil (buton döngüsü)
    circleMode = 2, squareMode = 0,
  } = opts;

  const entities = [];
  let hc = 0;
  const h = () => `${handlePrefix}${(hc++).toString(16).toUpperCase()}`;

  if (drawFrame) {
    const x0 = originX, y0 = originY, x1 = originX + width, y1 = originY + height;
    entities.push(line(h(), x0, y0, x1, y0, layer));
    entities.push(line(h(), x1, y0, x1, y1, layer));
    entities.push(line(h(), x1, y1, x0, y1, layer));
    entities.push(line(h(), x0, y1, x0, y0, layer));
  }

  // Font yüksekliği hücre yüksekliğine oranlı (280 → ~14); konumlar oransal →
  // Marka hücresi hangi boyutta olursa olsun doğru yerleşir.
  const fh = Math.max(6, height * 0.05);
  // Bloklar Marka alanının ALTINA hizalanır (Nef header düzeni). MTEXT top-left
  // anchor olduğundan blok yüksekliğini hesaplayıp üst-y'yi ona göre koyarız →
  // blok altı hücre tabanından `bottomMargin` yukarıda biter.
  const bottomMargin = height * 0.10;
  const blockHeight = (n, lh) => n * lh * 1.45;

  // Marka İSMİ bloğu (SOL — logo yerine metin): ilk satır BÜYÜK + altında 2 satır,
  // bir DAİRE içinde ve DİKEY ORTALI (Nef header'daki logo düzeni).
  const brand = markaData.brand || {};
  const hasBrandText = brand.isim || brand.satir1 || brand.satir2;
  if (hasBrandText || circleMode > 0 || squareMode > 0) {
    const bigH = fh * 1.7;
    const smallH = fh * 0.66;
    // Sol bölge, dikey merkez. Yarıçap hücreye sığacak şekilde sınırlı.
    const r = Math.min(width * 0.10, height * 0.40);
    const cx = originX + width * 0.02 + r;
    const cy = originY + height * 0.5;
    // Şekiller — mode 1: iç, mode 2: iç+dış (buton döngüsü). İç yarıçap r*0.88.
    if (circleMode >= 1) entities.push(circle(h(), cx, cy, r * 0.88, layer));
    if (circleMode >= 2) entities.push(circle(h(), cx, cy, r, layer));
    if (squareMode >= 1) pushSquare(entities, h, cx, cy, r * 0.88, layer);
    if (squareMode >= 2) pushSquare(entities, h, cx, cy, r, layer);

    // Büyük isim — merkezin biraz üstünde (attach 5 = orta-merkez)
    if (brand.isim) {
      entities.push(mtext(h(), cx, cy + bigH * 0.35, [{ text: brand.isim, bold: true }],
        { height: bigH, width: r * 1.7, layer, attach: 5 }));
    }
    // Alt satırlar — büyük satırın altında (bir satır boyu aşağı → çakışma yok)
    const subLines = [];
    if (brand.satir1) subLines.push({ text: brand.satir1 });
    if (brand.satir2) subLines.push({ text: brand.satir2 });
    if (subLines.length) {
      entities.push(mtext(h(), cx, cy - bigH * 1.1, subLines,
        { height: smallH, width: r * 1.7, layer, attach: 5 }));
    }
  }

  // Firma bloğu (orta) — sol ~%25 logo/marka-isim alanı için boş bırakılır
  const firmaW = width * 0.48;
  const fLines = firmaLines(markaData.firma);
  if (fLines.length) {
    const vn = estimateVisualLines(fLines, firmaW, fh);
    const topY = originY + bottomMargin + blockHeight(vn, fh);
    entities.push(mtext(h(), originX + width * 0.26, topY, fLines,
      { height: fh, width: firmaW, layer }));
  }
  // Sorumlu bloğu (sağ)
  const rfh = fh * 0.82;
  const sorumluW = width * 0.22;
  const sLines = sorumluLines(markaData.sorumlu);
  if (sLines.length) {
    const vn = estimateVisualLines(sLines, sorumluW, rfh);
    const topY = originY + bottomMargin + blockHeight(vn, rfh);
    entities.push(mtext(h(), originX + width * 0.76, topY, sLines,
      { height: rfh, width: sorumluW, layer }));
  }

  return entities;
}

export default buildMarkaEntities;
